import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./Schedule.module.css";
import Button from "../button/Button";
import Select from "../select";
import DatePicker from "../date-picker";
import CheckBox from "../input/check-box";
import generateCron from "./generateCron";
import {
  FormInput,
  FormInputContainer,
  FormInputLabel,
} from "app/common/form/FormInputBlock";

export const getUserTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const aliases = {
    "Asia/Calcutta": "Asia/Kolkata",
  };
  return aliases[timeZone] || timeZone;
};

export const commonTimeZones = [
  "Pacific/Pago_Pago",
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Halifax",
  "America/St_Johns",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Atlantic/Azores",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Helsinki",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Jerusalem",
  "Asia/Tehran",
  "Asia/Dubai",
  "Asia/Kabul",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Kathmandu",
  "Asia/Dhaka",
  "Asia/Yangon",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Perth",
  "Australia/Adelaide",
  "Australia/Sydney",
  "Pacific/Guadalcanal",
  "Pacific/Auckland",
  "Pacific/Kiritimati",
];

/* ------------------------------------------------------------------ *
 * An incoming `value.startDate` (from a parent loading an existing
 * schedule from the backend) may arrive as a plain local wall-clock
 * string ("2026-08-14T16:39:00", no zone) or already a Date. Parse
 * the string form by hand instead of trusting `new Date(string)`,
 * which parses date-only vs date-time strings inconsistently
 * (UTC vs local system time depending on the exact shape).
 * ------------------------------------------------------------------ */
const LOCAL_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const toValidDate = (value, fallback = new Date()) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === "string") {
    const match = LOCAL_DATETIME_RE.exec(value);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        second ? Number(second) : 0,
      );
    }
  }

  const fallbackParsed = new Date(value);
  return Number.isNaN(fallbackParsed.getTime()) ? fallback : fallbackParsed;
};

// Serialize a Date back to "2026-08-14T16:39:00" using local getters -
// never .toISOString(), which converts to UTC. This is the ONE place
// the internal Date crosses into the plain wall-clock string the
// backend expects (SchedulerConfigDTO.startDateLocal).
const toLocalISOString = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
};

// The "Time Zone" dropdown stores the full display label
// ("(GMT +05:30) Asia/Kolkata") for the UI, but the backend needs just
// the IANA id ("Asia/Kolkata"). Extract it at the payload boundary.
const extractIanaZone = (label) => {
  const match = /\)\s*(.+)$/.exec(label || "");
  return match ? match[1].trim() : label;
};

const Schedule = ({
  onChange,
  value,
  needSchedulerName = false,
  children,
  needTitle = true,
  setDateOpened,
}) => {
  const [schedule, setSchedule] = useState({
    schedulerName: "",
    frequency: "Daily",
    startDate: new Date(), // Date internally, throughout this component tree
    timeZone: getUserTimeZone(),
    repeat: [0],
    perform: "week",
    occurs: [0, 0],
  });

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (onChange) {
      onChange({
        schedulerName: schedule.schedulerName,
        startDate: toLocalISOString(schedule.startDate), // plain wall-clock string, for the API
        timeZone: extractIanaZone(schedule.timeZone), // IANA id only, for the API
        cron: generateCron(schedule),
        frequency: schedule.frequency,
      });
    }
  }, [schedule]);

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!value || hydratedRef.current) return;
    hydratedRef.current = true;

    const labelForIanaZone = (ianaId) =>
      commonTimeZones.find((label) => label.endsWith(ianaId)) || ianaId;

    setSchedule((prev) => ({
      ...prev,
      ...value,
      startDate: toValidDate(value.startDate, prev.startDate),
      timeZone: value.timeZone
        ? labelForIanaZone(value.timeZone)
        : prev.timeZone,
    }));
  }, [value]);
  const handleScheduleChange = (key, value) => {
    setSchedule((prev) => ({ ...prev, [key]: value }));
  };

  const handleStartDateChange = useCallback((date) => {
    setSchedule((prev) => ({ ...prev, startDate: date }));
  }, []);

  const setRepeat = useCallback((value) => {
    setSchedule((prev) => ({ ...prev, repeat: value }));
  }, []);

  // Handle click outside of date
  const divRef = useRef(null);
  useEffect(() => {
    if (!setDateOpened) return;
    function handleClickOutside(event) {
      if (divRef.current && !divRef.current.contains(event.target))
        setDateOpened(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.schedule}>
      {needTitle && <h3>Scheduler Details</h3>}
      <div className={styles.scheduleContent}>
        {needSchedulerName && (
          <FormInputContainer>
            <FormInputLabel>
              <label>
                Scheduler Name <span style={{ color: "red" }}>*</span>
              </label>
            </FormInputLabel>
            <FormInput>
              <input
                className={styles.SchedulerNameInput}
                type="text"
                id="schedulerName"
                name="schedulerName"
                placeholder="Enter scheduler name"
                value={schedule.schedulerName}
                onChange={(e) => {
                  setSchedule((prev) => ({
                    ...prev,
                    schedulerName: e.target.value,
                  }));
                }}
              />
            </FormInput>
          </FormInputContainer>
        )}

        <FormInputContainer>
          <FormInputLabel>
            <label>Start Date</label>
          </FormInputLabel>
          <FormInput>
            <div
              ref={divRef}
              onClick={() => (setDateOpened ? setDateOpened(true) : null)}
            >
              <DatePicker
                value={schedule.startDate}
                onChange={handleStartDateChange}
              />
            </div>
          </FormInput>
        </FormInputContainer>

        <FormInputContainer>
          <FormInputLabel>
            <label>Time Zone</label>
          </FormInputLabel>
          <FormInput>
            <div>
              <Select
                options={commonTimeZones}
                active={commonTimeZones.findIndex(
                  (tz) => tz == schedule.timeZone,
                )}
                triggerProps={{
                  className: styles.select,
                }}
                onOptionSelect={(option, index) =>
                  handleScheduleChange("timeZone", option)
                }
                style={{ minHeight: "37px" }}
              />
            </div>
          </FormInput>
        </FormInputContainer>

        <FormInputContainer>
          <FormInputLabel>
            <label>Frequency</label>
          </FormInputLabel>
          <FormInput>
            <div className={styles.frequencyBtn}>
              <Button
                className={[
                  schedule.frequency === "Daily" && styles.activeFrequency,
                ].join(" ")}
                onClick={() => handleScheduleChange("frequency", "Daily")}
              >
                Daily
              </Button>
              <Button
                className={[
                  schedule.frequency === "Weekly" && styles.activeFrequency,
                ].join(" ")}
                onClick={() => handleScheduleChange("frequency", "Weekly")}
              >
                Weekly
              </Button>
              <Button
                className={[
                  schedule.frequency === "Monthly" && styles.activeFrequency,
                ].join(" ")}
                onClick={() => handleScheduleChange("frequency", "Monthly")}
              >
                Monthly
              </Button>
            </div>
          </FormInput>
        </FormInputContainer>

        {schedule.frequency === "Monthly" && (
          <>
            <FormInputContainer>
              <FormInputLabel>
                <label>Perform</label>
              </FormInputLabel>

              <FormInput>
                <div className={styles.perform}>
                  <div>
                    <input
                      type="radio"
                      name="perform"
                      id="Week"
                      value="Week"
                      checked={schedule.perform === "week"}
                      onChange={(e) => {
                        setSchedule((prev) => ({
                          ...prev,
                          perform: "week",
                          occurs: [0, 0],
                        }));
                      }}
                    />
                    <label htmlFor="Week">Week</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="perform"
                      id="Day"
                      value="Day"
                      checked={schedule.perform === "day"}
                      onChange={(e) => {
                        setSchedule((prev) => ({
                          ...prev,
                          perform: "day",
                          occurs: [0],
                        }));
                      }}
                    />
                    <label htmlFor="Day">Day</label>
                  </div>
                </div>
              </FormInput>
            </FormInputContainer>

            <FormInputContainer>
              <FormInputLabel>
                <label>Occurs</label>
              </FormInputLabel>
              <FormInput>
                <div
                  style={{ width: "max-content", display: "flex", gap: "1rem" }}
                >
                  {schedule.perform === "day" ? (
                    <Select
                      active={schedule.occurs[0]}
                      options={Array.from({ length: 31 }, (_, i) => i + 1)}
                      triggerProps={{
                        className: styles.select,
                      }}
                      childrenProps={{
                        style: {
                          display: "grid",
                          gridTemplateColumns: "repeat(6, 1fr)",
                        },
                      }}
                      onOptionSelect={(_option, index) =>
                        handleScheduleChange("occurs", [index])
                      }
                      style={{ minHeight: "37px" }}
                    />
                  ) : (
                    <div>
                      <Select
                        active={schedule.occurs[0]}
                        options={[
                          "First week",
                          "Second week",
                          "Third week",
                          "Fourth week",
                          "Fifth week",
                        ]}
                        triggerProps={{
                          className: styles.select,
                          style: { minWidth: "12.5rem", minHeight: "37px" },
                        }}
                        onOptionSelect={(_option, index) =>
                          handleScheduleChange("occurs", [
                            index,
                            schedule.occurs[1],
                          ])
                        }
                        style={{ minHeight: "37px", marginBottom: "10px" }}
                      />
                      <Select
                        active={schedule.occurs[1]}
                        options={[
                          "Sunday",
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                        ]}
                        triggerProps={{
                          className: styles.select,
                          style: { minWidth: "12.5rem" },
                        }}
                        onOptionSelect={(_option, index) =>
                          handleScheduleChange("occurs", [
                            schedule.occurs[0],
                            index,
                          ])
                        }
                        style={{ minHeight: "37px" }}
                      />
                    </div>
                  )}
                </div>
              </FormInput>
            </FormInputContainer>
          </>
        )}
        <Repeat
          frequency={schedule.frequency}
          setRepeat={setRepeat}
          repeat={
            value?.frequency === schedule.frequency ? value?.repeat : null
          }
        />
        {children}
      </div>
    </div>
  );
};

const Repeat = ({ frequency, setRepeat, repeat }) => {
  const [repeatArr, setRepeatArr] = useState([]);

  useEffect(() => {
    if (repeat) setRepeatArr(repeat);
    else if (frequency.toLowerCase() === "daily") setRepeatArr([0]);
    else setRepeatArr([]);
  }, [frequency, repeat]);

  useEffect(() => {
    setRepeat(repeatArr);
  }, [repeatArr, setRepeat]);

  const handleCheckedChange = (e, val) => {
    e.persist();
    setRepeatArr((prev) => {
      let newArr;
      if (e.target.checked) newArr = [...prev, val];
      else newArr = prev.filter((existingVal) => existingVal !== val);
      return newArr;
    });
  };

  const weeklyOptions = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const monthlyOptions = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const renderRepeatOptions = () => {
    switch (frequency) {
      case "Daily":
        return (
          <Select
            options={["Everyday", "Alternative days", "Week days"]}
            triggerProps={{ className: styles.select }}
            active={repeatArr[0]}
            onOptionSelect={(_option, index) => setRepeatArr([index])}
            style={{ minHeight: "37px" }}
          />
        );
      case "Weekly":
        return (
          <div className={styles.repeatContainer}>
            {weeklyOptions.map((day, index) => (
              <CheckBox
                label={day}
                key={day}
                scaleIcon={0.75}
                checked={repeatArr.includes(index)}
                onChange={(e) => handleCheckedChange(e, index)}
              />
            ))}
          </div>
        );
      case "Monthly":
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.4rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                rowGap: "1rem",
              }}
            >
              {monthlyOptions.map((day, index) => (
                <CheckBox
                  label={day}
                  key={day}
                  scaleIcon={0.75}
                  checked={repeatArr.includes(index + 1)}
                  onChange={(e) => handleCheckedChange(e, index + 1)}
                />
              ))}
            </div>
          </div>
        );
      case "Custom":
        return (
          <>
            <Button>Every 1 day</Button>
            <Button>Every 1 week</Button>
            <Button>Every 1 month</Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <FormInputContainer>
      <FormInputLabel>
        <label>Repeat</label>
      </FormInputLabel>
      <FormInput>{renderRepeatOptions()}</FormInput>
    </FormInputContainer>
  );
};

export default Schedule;
